import type {
  DeleteRateLimitResponse,
  EffectiveRateLimitResponse,
  GetGlobalRateLimitResponse,
  OrgEffectiveRateLimitResponse,
  ListAllOrgTaskAdmissionOverridesResponse,
  ListOrgRateLimitsResponse,
  ListOrgSkuRateLimitsResponse,
  ListRateLimitRefusalsResponse,
  ListRoutingLoadScopeSettingsResponse,
  ListSkuRateLimitsResponse,
  PutGlobalRateLimitResponse,
  PutOrgRateLimitResponse,
  PutOrgSkuRateLimitResponse,
  PutRateLimitRequest,
  PutRoutingLoadScopeSettingsResponse,
  PutSkuRateLimitResponse,
  QuotaBypassState,
  RoutingLoadOverviewResponse,
  RoutingLoadScopeKind,
  RoutingLoadSettingsResponse,
  UpdateRoutingLoadScopeSettingsRequest,
  UpdateRoutingLoadSettingsRequest,
  UpdateRoutingLoadSettingsResponse,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/routing-load';

export async function getRoutingLoadSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RoutingLoadSettingsResponse> {
  return opsApiRequest<RoutingLoadSettingsResponse>(`${BASE}/settings`, { signal }, fetchImpl);
}

export async function updateRoutingLoadSettings(
  body: UpdateRoutingLoadSettingsRequest,
  fetchImpl: typeof fetch,
): Promise<UpdateRoutingLoadSettingsResponse> {
  return opsApiRequest<UpdateRoutingLoadSettingsResponse>(
    `${BASE}/settings`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function getRoutingLoadOverview(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RoutingLoadOverviewResponse> {
  return opsApiRequest<RoutingLoadOverviewResponse>(`${BASE}/overview`, { signal }, fetchImpl);
}

export async function listSkuRateLimits(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListSkuRateLimitsResponse> {
  return opsApiRequest<ListSkuRateLimitsResponse>(`${BASE}/rate-limits/sku`, { signal }, fetchImpl);
}

export async function putSkuRateLimit(
  skuId: string,
  body: PutRateLimitRequest,
  fetchImpl: typeof fetch,
): Promise<PutSkuRateLimitResponse> {
  return opsApiRequest<PutSkuRateLimitResponse>(
    `${BASE}/rate-limits/sku/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteSkuRateLimit(
  skuId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRateLimitResponse> {
  return opsApiRequest<DeleteRateLimitResponse>(
    `${BASE}/rate-limits/sku/${encodeURIComponent(skuId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listOrgSkuRateLimits(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgSkuRateLimitsResponse> {
  return opsApiRequest<ListOrgSkuRateLimitsResponse>(
    `${BASE}/rate-limits/org/${encodeURIComponent(orgId)}`,
    { signal },
    fetchImpl,
  );
}

export async function putOrgSkuRateLimit(
  orgId: string,
  skuId: string,
  body: PutRateLimitRequest,
  fetchImpl: typeof fetch,
): Promise<PutOrgSkuRateLimitResponse> {
  return opsApiRequest<PutOrgSkuRateLimitResponse>(
    `${BASE}/rate-limits/org/${encodeURIComponent(orgId)}/${encodeURIComponent(skuId)}`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteOrgSkuRateLimit(
  orgId: string,
  skuId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRateLimitResponse> {
  return opsApiRequest<DeleteRateLimitResponse>(
    `${BASE}/rate-limits/org/${encodeURIComponent(orgId)}/${encodeURIComponent(skuId)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function getGlobalRateLimit(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<GetGlobalRateLimitResponse> {
  return opsApiRequest<GetGlobalRateLimitResponse>(`${BASE}/rate-limits/global`, { signal }, fetchImpl);
}

export async function putGlobalRateLimit(
  body: PutRateLimitRequest,
  fetchImpl: typeof fetch,
): Promise<PutGlobalRateLimitResponse> {
  return opsApiRequest<PutGlobalRateLimitResponse>(
    `${BASE}/rate-limits/global`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteGlobalRateLimit(
  fetchImpl: typeof fetch,
): Promise<DeleteRateLimitResponse> {
  return opsApiRequest<DeleteRateLimitResponse>(
    `${BASE}/rate-limits/global`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listOrgRateLimits(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgRateLimitsResponse> {
  return opsApiRequest<ListOrgRateLimitsResponse>(`${BASE}/rate-limits/org`, { signal }, fetchImpl);
}

export async function listAllOrgSkuRateLimits(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOrgSkuRateLimitsResponse> {
  return opsApiRequest<ListOrgSkuRateLimitsResponse>(`${BASE}/rate-limits/org-sku`, { signal }, fetchImpl);
}

export async function putOrgRateLimit(
  orgId: string,
  body: PutRateLimitRequest,
  fetchImpl: typeof fetch,
): Promise<PutOrgRateLimitResponse> {
  return opsApiRequest<PutOrgRateLimitResponse>(
    `${BASE}/rate-limits/org/${encodeURIComponent(orgId)}/all`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteOrgRateLimit(
  orgId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRateLimitResponse> {
  return opsApiRequest<DeleteRateLimitResponse>(
    `${BASE}/rate-limits/org/${encodeURIComponent(orgId)}/all`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function getEffectiveRateLimit(
  orgId: string,
  skuId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<EffectiveRateLimitResponse> {
  const qs = new URLSearchParams({ org_id: orgId, sku_id: skuId }).toString();
  return opsApiRequest<EffectiveRateLimitResponse>(
    `${BASE}/rate-limits/effective?${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function getOrgEffectiveRateLimits(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OrgEffectiveRateLimitResponse> {
  const qs = new URLSearchParams({ org_id: orgId }).toString();
  return opsApiRequest<OrgEffectiveRateLimitResponse>(
    `${BASE}/rate-limits/effective?${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function listRateLimitRefusals(
  since: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRateLimitRefusalsResponse> {
  const qs = new URLSearchParams({ since }).toString();
  return opsApiRequest<ListRateLimitRefusalsResponse>(
    `${BASE}/rate-limits/refusals?${qs}`,
    { signal },
    fetchImpl,
  );
}

export async function getQuotaBypass(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<QuotaBypassState> {
  return opsApiRequest<QuotaBypassState>(`${BASE}/quota-bypass`, { signal }, fetchImpl);
}

export async function putQuotaBypass(
  enabled: boolean,
  fetchImpl: typeof fetch,
): Promise<QuotaBypassState> {
  return opsApiRequest<QuotaBypassState>(
    `${BASE}/quota-bypass`,
    { method: 'PUT', body: { enabled } },
    fetchImpl,
  );
}

export async function listAllTaskAdmissionOverrides(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListAllOrgTaskAdmissionOverridesResponse> {
  return opsApiRequest<ListAllOrgTaskAdmissionOverridesResponse>(
    `${BASE}/task-admission-overrides`,
    { signal },
    fetchImpl,
  );
}

function scopeSettingsPath(kind: RoutingLoadScopeKind, scopeId: string): string {
  return `${BASE}/scope-settings/${encodeURIComponent(kind)}/${encodeURIComponent(scopeId)}`;
}

export async function listRoutingLoadScopeSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListRoutingLoadScopeSettingsResponse> {
  return opsApiRequest<ListRoutingLoadScopeSettingsResponse>(
    `${BASE}/scope-settings`,
    { signal },
    fetchImpl,
  );
}

export async function putRoutingLoadScopeSettings(
  kind: RoutingLoadScopeKind,
  scopeId: string,
  body: UpdateRoutingLoadScopeSettingsRequest,
  fetchImpl: typeof fetch,
): Promise<PutRoutingLoadScopeSettingsResponse> {
  return opsApiRequest<PutRoutingLoadScopeSettingsResponse>(
    scopeSettingsPath(kind, scopeId),
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteRoutingLoadScopeSettings(
  kind: RoutingLoadScopeKind,
  scopeId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteRateLimitResponse> {
  return opsApiRequest<DeleteRateLimitResponse>(
    scopeSettingsPath(kind, scopeId),
    { method: 'DELETE' },
    fetchImpl,
  );
}
